import React, { useState } from "react";
import { toast } from "sonner";
import { finApi, finErr, money } from "@/lib/financeApi";
import { btnGold, field } from "@/components/nds/windows/finance/parts";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";
import WorkflowContent from "@/components/nds/windows/finance/WorkflowContent";

const ACCOUNTS = [["operating", "Operating"], ["reserve", "Reserve"], ["other", "Other"]];
const TYPES = [["deposit", "Deposit"], ["withdrawal", "Withdrawal"], ["transfer", "Transfer"], ["fee", "Fee"], ["adjustment", "Adjustment"]];
const KEYS = ["entry_date", "bank_account", "transaction_type", "description", "amount", "ref_number", "notes"];

function EditEntry({ rec, reload }) {
  const isSuper = useIsSuper();
  const [f, setF] = useState(() => Object.fromEntries(KEYS.map((k) => [k, String(rec[k] ?? "")])));
  const [busy, setBusy] = useState(false);
  if (!isSuper || rec.status !== "unreconciled") return null;
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try { await finApi.patch(`/bank-ledger/${rec.id}`, { ...f, amount: Number(f.amount) }); toast.success("Entry updated"); reload(); }
    catch (e) { toast.error(finErr(e)); } finally { setBusy(false); }
  };
  const input = (k, type = "text") => <input type={type} value={f[k]} onChange={set(k)} data-testid={`fin-bl-edit-${k}`} min={type === "number" ? "0.01" : undefined} step={type === "number" ? "0.01" : undefined} className={`${field} !py-1 w-full`} />;
  const select = (k, opts) => <select value={f[k]} onChange={set(k)} data-testid={`fin-bl-edit-${k}`} className={`${field} !py-1 w-full`}>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
  return (
    <div data-testid="fin-bl-edit" className="p-4 border border-white/10 rounded-sm space-y-3">
      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Edit entry · unreconciled only</span>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {input("entry_date", "date")}{select("bank_account", ACCOUNTS)}{select("transaction_type", TYPES)}
        {input("amount", "number")}{input("ref_number")}{input("description")}<div className="md:col-span-3">{input("notes")}</div>
      </div>
      <div className="flex justify-end"><button type="button" disabled={busy || !f.description.trim() || !(Number(f.amount) > 0)} onClick={save} data-testid="fin-bl-edit-save" className={btnGold}>Save changes</button></div>
    </div>
  );
}

const CFG = {
  key: "bank_ledger", api: "/bank-ledger", testid: "fin-bl", label: "Bank entry", plural: "bank entries", refType: "bank_ledger", saOnly: true, noLedger: true, docLocked: ["reconciled"],
  summaryTone: "yellow", summaryText: (s) => `${s.count} unreconciled · ${money(s.amount)}`,
  chips: { unreconciled: ["yellow", "Unreconciled"], reconciled: ["green", "Reconciled"], voided: ["darkgrey", "Voided"] },
  form: [{ k: "entry_date", label: "Entry date", type: "date" }, { k: "bank_account", label: "Account", type: "select", options: ACCOUNTS },
    { k: "transaction_type", label: "Type", type: "select", options: TYPES }, { k: "amount", label: "Amount", type: "number" },
    { k: "ref_number", label: "Ref / check #", type: "text", optional: true }, { k: "description", label: "Description", type: "text" },
    { k: "notes", label: "Notes", type: "text", optional: true, wide: true }],
  actions: [
    { name: "reconcile", label: "Mark reconciled", from: ["unreconciled"], sa: true, tone: "green" },
    { name: "void", label: "Void", from: ["unreconciled"], sa: true, tone: "red", note: true, placeholder: "Void reason (required)" },
  ],
  row: (r) => <><span className="text-nex-silver font-medium">{r.description}</span><span className="font-mono text-[10px] text-nex-muted">{r.entry_date}</span><span className="font-mono text-[10px] uppercase text-nex-muted">{r.bank_account} · {r.transaction_type}</span></>,
  amount: (r) => money(r.amount),
  title: (r) => r.description,
  fields: (r) => [["Entry date", r.entry_date], ["Account", r.bank_account], ["Type", r.transaction_type], ["Amount", money(r.amount)], ["Ref / check #", r.ref_number],
    ["Reconciled", r.reconciled_at && `${r.reconciled_by?.name} · ${r.reconciled_at.slice(0, 10)}`], ["Notes", r.notes]],
  Extra: ({ rec, reload }) => <EditEntry key={`${rec.id}-${rec.history.length}`} rec={rec} reload={reload} />,
};
export default function FinBankLedgerContent() { return <WorkflowContent cfg={CFG} />; }
